import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  FileUploader,
  Icon,
  IconButton,
  Input,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { galleryCaptionSchema } from '@/validations';

import { useApplyBranding } from './cache';
import { BRAND_IMAGE_TYPES, MAX_BRAND_IMAGE_BYTES } from './CoverEditor';

/** Matches MAX_COMPANY_GALLERY_ITEMS on the backend. */
export const MAX_GALLERY_PHOTOS = 8;

/**
 * Up to eight photos with captions. Each change is saved as it is made, and
 * the order is sent as the complete list of ids, which is what the API needs
 * to accept it.
 */
export function GalleryEditor({ branding, readOnly = false }) {
  const toast = useToast();
  const applyBranding = useApplyBranding();
  const [caption, setCaption] = useState('');
  const [captionError, setCaptionError] = useState(null);
  const [removing, setRemoving] = useState(null);

  const gallery = [...(branding.gallery ?? [])].sort((a, b) => a.position - b.position);
  const full = gallery.length >= MAX_GALLERY_PHOTOS;

  function onError(fallback) {
    return (error) => toast.error(errorMessage(error, fallback));
  }

  const add = useMutation({
    mutationFn: ({ file, text }) => companyApi.addGalleryPhoto(file, text),
    onSuccess: (updated) => {
      applyBranding(updated);
      setCaption('');
      toast.success('Photo added');
    },
    onError: onError('Could not add the photo'),
  });

  const reorder = useMutation({
    mutationFn: (ids) => companyApi.reorderGallery(ids),
    onSuccess: applyBranding,
    onError: onError('Could not reorder the gallery'),
  });

  const remove = useMutation({
    mutationFn: (id) => companyApi.removeGalleryPhoto(id),
    onSuccess: (updated) => {
      applyBranding(updated);
      setRemoving(null);
      toast.success('Photo removed');
    },
    onError: onError('Could not remove the photo'),
  });

  function upload(file) {
    const parsed = galleryCaptionSchema.safeParse(caption);
    if (!parsed.success) {
      setCaptionError(parsed.error.issues[0].message);
      return;
    }
    setCaptionError(null);
    add.mutate({ file, text: parsed.data || undefined });
  }

  function move(index, offset) {
    const ids = gallery.map((photo) => photo.id);
    const target = index + offset;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  }

  return (
    <Card>
      <CardHeader
        title="Gallery"
        description={`${gallery.length} of ${MAX_GALLERY_PHOTOS} photos. The office, the team, a day at work.`}
      />

      {gallery.length > 0 && (
        <ol className="mb-4 space-y-3" aria-label="Gallery photos">
          {gallery.map((photo, index) => (
            <li
              key={photo.id}
              className="border-line rounded-input flex flex-wrap items-start gap-3 border p-3"
            >
              <img
                src={resolveFileUrl(photo.url)}
                alt={photo.caption || `Photo ${index + 1}`}
                className="border-line size-20 shrink-0 rounded-lg border object-cover"
              />
              <div className="min-w-0 flex-1">
                <CaptionEditor photo={photo} index={index} readOnly={readOnly} />
              </div>
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Move photo ${index + 1} up`}
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(index, -1)}
                  >
                    <Icon name="arrowUp" size={14} />
                    Move up
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Move photo ${index + 1} down`}
                    disabled={index === gallery.length - 1 || reorder.isPending}
                    onClick={() => move(index, 1)}
                  >
                    <Icon name="arrowUp" size={14} className="rotate-180" />
                    Move down
                  </Button>
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    title={`Remove photo ${index + 1}`}
                    onClick={() => setRemoving(photo)}
                  >
                    <Icon name="trash" size={14} />
                    Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {gallery.length === 0 && (
        <p className="text-ink-muted mb-4 text-sm">No photos yet. Three or more fill the grid.</p>
      )}

      {!readOnly &&
        (full ? (
          <Alert tone="info" title="The gallery is full">
            {MAX_GALLERY_PHOTOS} photos is the most a page shows. Remove one to add another.
          </Alert>
        ) : (
          <div className="space-y-3">
            <Input
              label="Caption for the next photo"
              placeholder="Friday demo day"
              maxLength={200}
              value={caption}
              error={captionError ?? undefined}
              onChange={(event) => setCaption(event.target.value)}
            />
            <FileUploader
              accept={BRAND_IMAGE_TYPES}
              maxSizeBytes={MAX_BRAND_IMAGE_BYTES}
              isUploading={add.isPending}
              hint="JPEG, PNG or WebP, up to 5 MB."
              onSelect={upload}
            />
          </div>
        ))}

      <ConfirmDialog
        open={removing !== null}
        title="Remove this photo?"
        message="It disappears from your public page straight away."
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </Card>
  );
}

/** A caption saved on its own button, so typing never fires a request. */
function CaptionEditor({ photo, index, readOnly }) {
  const toast = useToast();
  const applyBranding = useApplyBranding();
  const [draft, setDraft] = useState(photo.caption ?? '');
  const [error, setError] = useState(null);
  const changed = draft.trim() !== (photo.caption ?? '');

  const save = useMutation({
    mutationFn: (text) => companyApi.updateGalleryPhoto(photo.id, text),
    onSuccess: (updated) => {
      applyBranding(updated);
      toast.success('Caption saved');
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save the caption')),
  });

  if (readOnly) {
    return <p className="text-ink text-sm">{photo.caption || 'No caption'}</p>;
  }

  function submit() {
    const parsed = galleryCaptionSchema.safeParse(draft);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setError(null);
    save.mutate(parsed.data || null);
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Input
        label={`Caption for photo ${index + 1}`}
        hideLabel
        size="sm"
        placeholder="Add a caption"
        maxLength={200}
        value={draft}
        error={error ?? undefined}
        wrapperClassName="min-w-0 flex-1"
        onChange={(event) => setDraft(event.target.value)}
      />
      <IconButton
        icon="check"
        variant="ghostPositive"
        label={`Save the caption for photo ${index + 1}`}
        disabled={!changed}
        isLoading={save.isPending}
        onClick={submit}
      />
    </div>
  );
}
